import React from 'react';
import Navbar from 'src/components/Navbar';
import Footer from 'src/components/Footer';
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  DollarSign,
  Clock,
  Users,
  TrendingUp,
  BookOpen,
  Calendar,
  Heart,
  ArrowRight,
  BarChart3,
  Globe,
  Lightbulb,
  Shield,
  Sparkles,
  Quote
} from "lucide-react";

const Tutors = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="text-gray-900 pt-12 md:pt-16 pb-20 px-4 sm:px-6 lg:px-8 mt-10 md:mt-12 bg-gradient-to-br from-white via-orange-50/20 to-purple-50/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="outline" className="bg-rfpurple-100 text-rfpurple-700 border-rfpurple-300 mb-6">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Join Our Elite Tutor Network
                </Badge>
                <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                  Empower Students,
                  <span className="block text-rfpurple-600">Grow Your Career</span>
                </h1>
                <p className="text-xl mb-8 text-gray-600 leading-relaxed">
                  Join rfLearn's innovative platform where passionate educators connect with motivated students
                  through personalized, AI-enhanced learning experiences.
                </p>
                <div className="flex justify-start">
                  <Button size="lg" className="bg-rfpurple-600 text-white hover:bg-rfpurple-700 font-semibold" asChild>
                    <Link to="/become-tutor">
                      <GraduationCap className="mr-2 h-5 w-5" />
                      Become a Tutor
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <section aria-labelledby="tutor-toolkit-title" className="overflow-hidden rounded-2xl bg-[#fff1eb] p-6 text-[#24120d] shadow-[0_24px_55px_-30px_rgba(198,79,40,0.2)] sm:p-8">
                  <div className="flex items-start justify-between gap-5">
                    <div>
                      <h2 id="tutor-toolkit-title" className="text-2xl font-bold tracking-[-0.03em] sm:text-3xl">Your tutor toolkit</h2>
                      <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#552317]">Everything you need to plan strong lessons, teach with focus, and grow your practice.</p>
                    </div>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ffe0d4] text-[#9c3a20] ring-1 ring-[#f3c8bb]">
                      <GraduationCap className="h-5 w-5" aria-hidden="true" />
                    </div>
                  </div>

                  <div className="mt-8 border-t border-[#f3c8bb]">
                    <div className="group flex gap-4 py-5">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b84323] text-white">
                        <BookOpen className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="font-bold tracking-[-0.015em]">Curriculum and lesson support</h3>
                        <p className="mt-1 text-sm leading-relaxed text-[#552317]">Teaching materials and resources for every lesson.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 border-t border-[#f3c8bb] sm:grid-cols-2">
                      <div className="border-b border-[#f3c8bb] py-5 sm:border-b-0 sm:border-r sm:pr-5">
                        <Calendar className="mb-3 h-5 w-5 text-[#b84323]" aria-hidden="true" />
                        <h3 className="font-bold tracking-[-0.015em]">Flexible scheduling</h3>
                        <p className="mt-1 text-sm text-[#552317]">Set your availability around your life.</p>
                      </div>
                      <div className="border-b border-[#f3c8bb] py-5 sm:border-b-0 sm:pl-5">
                        <Users className="mb-3 h-5 w-5 text-[#b84323]" aria-hidden="true" />
                        <h3 className="font-bold tracking-[-0.015em]">Quality student matches</h3>
                        <p className="mt-1 text-sm text-[#552317]">Connect with motivated learners.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 border-t border-[#f3c8bb] sm:grid-cols-3">
                      <div className="flex items-center gap-2.5 py-4 sm:border-r sm:border-[#f3c8bb] sm:pr-4">
                        <BarChart3 className="h-4 w-4 shrink-0 text-[#b84323]" aria-hidden="true" />
                        <span className="text-sm font-semibold">Progress insights</span>
                      </div>
                      <div className="flex items-center gap-2.5 border-t border-[#f3c8bb] py-4 sm:border-t-0 sm:border-r sm:border-[#f3c8bb] sm:px-4">
                        <DollarSign className="h-4 w-4 shrink-0 text-[#b84323]" aria-hidden="true" />
                        <span className="text-sm font-semibold">Competitive earnings</span>
                      </div>
                      <div className="flex items-center gap-2.5 border-t border-[#f3c8bb] py-4 sm:border-t-0 sm:pl-4">
                        <Shield className="h-4 w-4 shrink-0 text-[#b84323]" aria-hidden="true" />
                        <span className="text-sm font-semibold">24/7 support</span>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section id="benefits" className="bg-rfpurple-50 py-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end lg:gap-10">
              <h2 className="max-w-md text-3xl font-extrabold leading-[1.08] tracking-[-0.03em] text-gray-900 md:text-4xl lg:col-span-5">
                What we offer our tutors
              </h2>
              <p className="max-w-2xl text-base leading-relaxed text-gray-600 lg:col-span-7 lg:pb-0.5">
                Join a platform designed to support your success with cutting-edge tools,
                competitive compensation, and meaningful student connections.
              </p>
            </div>

            <div className="mt-9 grid grid-cols-1 border-y border-rfpurple-200 md:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    icon: DollarSign,
                    title: 'Competitive Earnings',
                    description: 'Experienced and qualified tutors, especially those with teaching certifications, may earn more.'
                  },
                  {
                    icon: Clock,
                    title: 'Flexible Scheduling',
                    description: 'Set your own hours and availability to match your lifestyle.'
                  },
                  {
                    icon: Users,
                    title: 'Quality Student Matches',
                    description: 'Work with motivated students who are serious about learning.'
                  },
                  {
                    icon: BarChart3,
                    title: 'Performance Analytics',
                    description: 'Track your impact with detailed student progress insights.'
                  },
                  {
                    icon: BookOpen,
                    title: 'Curriculum Support',
                    description: 'Access comprehensive teaching materials and resources.'
                  },
                  {
                    icon: Shield,
                    title: 'Professional Support',
                    description: '24/7 technical support and dedicated tutor success team.'
                  }
                ].map((offer) => {
                  const IconComponent = offer.icon;
                  return (
                    <article key={offer.title} className="group relative border-b border-rfpurple-200 px-1 py-5 transition-[background-color,transform] duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] hover:z-10 hover:scale-[1.025] hover:bg-white/90 motion-reduce:transform-none motion-reduce:transition-none last:border-b-0 md:px-6 md:odd:border-r md:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-last-child(-n+3)]:border-b-0">
                      <div className="mb-3 flex items-center gap-3">
                        <IconComponent className="h-5 w-5 shrink-0 text-rfpurple-700 transition-[color,transform] duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-0.5 group-hover:scale-110 group-hover:text-rfpurple-900 motion-reduce:transform-none motion-reduce:transition-none" aria-hidden="true" />
                        <h3 className="text-lg font-bold leading-tight tracking-[-0.02em] text-gray-900 transition-colors duration-300 group-hover:text-rfpurple-800 motion-reduce:transition-none">{offer.title}</h3>
                      </div>
                      <p className="max-w-sm text-sm leading-6 text-gray-600">{offer.description}</p>
                    </article>
                  );
                })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-[#fffaf7] px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-20">
              <div>
                <h2 className="max-w-xl text-4xl font-extrabold leading-[1.06] tracking-[-0.035em] text-gray-900 sm:text-5xl">
                  Why Tutors Choose rfLearn
                </h2>
                <p className="mt-5 max-w-xl text-lg leading-relaxed text-gray-600">
                  Experience the difference of teaching on a platform built specifically
                  for educator success and student achievement.
                </p>

                <div className="mt-10 grid border-t border-[#dfd6d1] sm:grid-cols-2 sm:gap-x-8">
                  {[
                    {
                      icon: TrendingUp,
                      title: 'Career Growth',
                      description: 'Build your reputation with student reviews and performance metrics'
                    },
                    {
                      icon: Globe,
                      title: 'Global Reach',
                      description: 'Teach students from anywhere with our virtual classroom technology'
                    },
                    {
                      icon: Heart,
                      title: 'Meaningful Impact',
                      description: 'Make a real difference in students\' academic journeys and futures'
                    },
                    {
                      icon: Lightbulb,
                      title: 'Innovation',
                      description: 'Use AI-powered tools to enhance your teaching effectiveness'
                    }
                  ].map((benefit) => {
                    const IconComponent = benefit.icon;
                    return (
                      <article key={benefit.title} className="group border-b border-[#dfd6d1] py-5 sm:py-6">
                        <div className="flex items-center gap-3 text-rfpurple-700">
                          <IconComponent className="h-5 w-5 shrink-0 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 motion-reduce:transform-none" aria-hidden="true" />
                          <h3 className="text-lg font-bold tracking-[-0.02em] text-gray-900">{benefit.title}</h3>
                        </div>
                        <p className="mt-2 max-w-xs text-sm leading-6 text-gray-600">{benefit.description}</p>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="border-t-4 border-rfpurple-500 bg-[#f0ebff] px-7 py-9 sm:px-10 sm:py-11">
                <figure>
                  <div className="flex items-start justify-between gap-5">
                    <h3 className="text-xl font-bold tracking-[-0.025em] text-gray-900">Tutor Success Story</h3>
                    <Quote className="h-7 w-7 shrink-0 text-rfpurple-500" aria-hidden="true" />
                  </div>
                  <blockquote className="mt-10 text-2xl font-medium leading-[1.45] tracking-[-0.025em] text-gray-900 sm:text-[1.7rem]">
                    &ldquo;rfLearn has transformed my tutoring career. The platform&apos;s tools help me track student progress effectively, and I&apos;ve increased my earnings by 40% while working with amazing students.&rdquo;
                  </blockquote>
                  <figcaption className="mt-10 border-t border-rfpurple-200 pt-5 text-sm text-gray-600">
                    <div className="font-semibold text-gray-900">Sarah Johnson</div>
                    <div className="mt-1">Mathematics Tutor, 2+ years on rfLearn</div>
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Started Section */}
        <section className="overflow-hidden bg-[#f8f6ff] px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 border-b border-rfpurple-200 pb-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end lg:gap-16">
              <h2 className="max-w-xl text-4xl font-extrabold leading-[1.05] tracking-[-0.035em] text-gray-900 sm:text-5xl">
                Your journey to a more meaningful teaching practice starts here.
              </h2>
              <p className="max-w-md text-lg leading-relaxed text-gray-600 lg:pb-1">
                Join our tutor community in three clear steps, with support from your first application to your first student match.
              </p>
            </div>

            <ol className="relative mt-12 grid gap-10 md:grid-cols-3 md:gap-6 lg:mt-16 lg:gap-10">
              {[
                {
                  step: '01',
                  title: 'Apply online',
                  description: 'Tell us about your qualifications and teaching experience.',
                  icon: BookOpen,
                  accent: 'bg-[#e9e2ff] text-rfpurple-800 ring-[#d2c4ff]'
                },
                {
                  step: '02',
                  title: 'Complete onboarding',
                  description: 'Get acquainted with the platform and prepare to teach with confidence.',
                  icon: GraduationCap,
                  accent: 'bg-[#e7efff] text-[#376ca8] ring-[#c9daf6]'
                },
                {
                  step: '03',
                  title: 'Start teaching',
                  description: 'Meet your students and begin building the learning relationships that matter.',
                  icon: Users,
                  accent: 'bg-[#ffe7de] text-[#a84328] ring-[#facab9]'
                }
              ].map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <li key={step.step} className="group relative">
                    {index < 2 && (
                      <div className="absolute left-8 top-8 hidden h-px w-[calc(100%-0.85rem)] bg-rfpurple-200 md:block" aria-hidden="true">
                        <ArrowRight className="absolute -right-1.5 -top-2 h-4 w-4 text-rfpurple-300" strokeWidth={2.5} />
                      </div>
                    )}
                    <div className="min-w-0 pt-1 md:pt-0">
                      <div className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ring-1 ${step.accent} transition-[transform,box-shadow] duration-300 ease-out group-hover:-translate-y-1 group-hover:shadow-[0_14px_28px_-20px_rgba(76,53,145,0.7)] motion-reduce:transform-none motion-reduce:transition-none`}>
                        <IconComponent className="h-7 w-7" strokeWidth={1.8} aria-hidden="true" />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-xs font-extrabold text-gray-900 ring-1 ring-rfpurple-200">
                          {step.step}
                        </span>
                        <h3 className="text-xl font-bold tracking-[-0.025em] text-gray-900">{step.title}</h3>
                      </div>
                      <p className="mt-3 max-w-[30ch] text-sm leading-6 text-gray-600 sm:text-base">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="mt-14 flex flex-col gap-4 border-t border-rfpurple-200 pt-6 sm:mt-16 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-medium text-rfpurple-800">We&apos;ll guide you through every stage.</p>
              <Link to="/become-tutor" className="group inline-flex w-fit items-center gap-2 text-sm font-bold text-rfpurple-800 underline decoration-rfpurple-300 decoration-1 underline-offset-4 transition-colors hover:text-rfpurple-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfpurple-500 focus-visible:ring-offset-4">
                Begin your application
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transform-none" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-rfpurple-600 to-rfpurple-500">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Teaching Career?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join hundreds of educators who are making a meaningful impact while growing their careers on rfLearn.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-rfpurple-600 hover:bg-gray-100 font-semibold" asChild>
                <Link to="/become-tutor">
                  <GraduationCap className="mr-2 h-5 w-5" />
                  Apply to Become a Tutor
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-rfpurple-600 bg-transparent" asChild>
                <Link to="/inquiry?type=tutoring">
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule a Call
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Tutors;
